import { CloseOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { App, Button, Flex, Popconfirm } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { deleteContractDeleteThunk, getNetworkInfoThunk } from "tm-shared/api";
import { Contract as ContractType } from "tm-shared/types";
import { isPointChains } from "tm-shared/utils";

import { getPointContractSourceIds, usePoint } from "@/domains/point";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { BlockList } from "@/shared/pageComposition/BlockList";
import { Meta } from "@/shared/seo";
import { PrimaryText } from "@/shared/ui/Typography";

import { StyledAPContainer } from "../../../Styled";
import { ContractModel, getContractBlocks } from "./Contract.blocks";
import { getContractCapabilities } from "./Contract.policy";

const isDateTodayOrAfter = (date?: string | Date | null): boolean => {
    if (!date) return true;

    const input = dayjs(date);
    const today = dayjs().startOf("day");

    return input.isSame(today, "day") || input.isAfter(today, "day");
};

const Contract = () => {
    const { notification } = App.useApp();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { pointId, contractId } = useParams<{
        pointId: string;
        contractId: string;
    }>();

    const { contractTypesList, targetsContracts } = useAppSelector(
        (state) => state.offline,
    );
    const { role, networks } = useAppSelector((state) => state.user);

    const [editMode, setEditMode] = useState<boolean>(false);

    if (!pointId || !contractId) {
        throw new Error("Route params are missing");
    }

    const { point, pointLoading } = usePoint(pointId);

    const capabilities = useMemo(() => getContractCapabilities(role), [role]);

    const contract = useMemo<ContractType | undefined>(() => {
        if (!point || !targetsContracts) return undefined;

        const contractSources = getPointContractSourceIds(point);

        for (const sourceId of contractSources) {
            const sourceContracts = targetsContracts[sourceId!];
            if (sourceContracts) {
                const foundContract = Object.values(sourceContracts).find(
                    (item: ContractType) => item.id === contractId,
                );
                if (foundContract) {
                    return foundContract;
                }
            }
        }

        return undefined;
    }, [point, targetsContracts, contractId]);

    const handleDeleteContract = async () => {
        try {
            await dispatch(deleteContractDeleteThunk({ contractId })).unwrap();

            navigate(-1);

            notification.success({
                title: "Контракт видалений",
            });
        } catch (error) {
            console.error("Error deleting contract:", error);
            notification.error({
                title: "Помилка при видаленні контракту",
            });
        }
    };

    const handleCancelEdit = () => {
        setEditMode(false);
    };

    const handleStartEdit = () => {
        setEditMode(true);
    };

    useEffect(() => {
        if (
            point &&
            isPointChains(point) &&
            !networks?.[point.typeContext.networkId]
        ) {
            dispatch(
                getNetworkInfoThunk({ network: point.typeContext.networkId }),
            );
        }
    }, [dispatch, networks, point]);

    const fopName = point?.market?.contragent?.name ?? "Невідомий ринок";
    const contractTypeName =
        (contract ? contractTypesList?.[contract.type] : undefined) ??
        "Невідомий вид контракту";

    const model: ContractModel | null = useMemo(() => {
        if (!contract) return null;
        return {
            capabilities,
            contract,
            fopName,
            contractTypeName,
            point,
            editMode,
        };
    }, [capabilities, contract, fopName, contractTypeName, point, editMode]);

    const blocks = useMemo(
        () => (model ? getContractBlocks(model) : []),
        [model],
    );

    if (pointLoading) return <Loading />;

    if (!contract || !model) {
        return (
            <>
                <Meta title="Помилка" />
                <Header title="Контракт не знайдено" back titleFontSize={16} />
                <StyledAPContainer>
                    <PrimaryText
                        fontSize={16}
                        fontWeight="400"
                        style={{ textAlign: "center" }}
                    >
                        Контракт не знайдено або видалено.
                    </PrimaryText>
                </StyledAPContainer>
            </>
        );
    }

    const canEdit = !editMode && isDateTodayOrAfter(contract.endDate);

    return (
        <>
            <Meta title="TT Info Contract" />
            <Header
                title={contract.name}
                back
                titleFontSize={16}
                rightActions={
                    capabilities.canManageContract ? (
                        <Flex gap={4}>
                            {canEdit && (
                                <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    onClick={handleStartEdit}
                                    aria-label="Редагувати контракт"
                                />
                            )}

                            {editMode && (
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    onClick={handleCancelEdit}
                                    aria-label="Скасувати редагування"
                                />
                            )}

                            <Popconfirm
                                title="Видалити контракт"
                                description="Ви впевнені що хочете видалити контракт?"
                                onConfirm={handleDeleteContract}
                                okText="Так"
                                cancelText="Ні"
                            >
                                <Button
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    aria-label="Видалити контракт"
                                />
                            </Popconfirm>
                        </Flex>
                    ) : null
                }
            />
            <StyledAPContainer>
                <BlockList blocks={blocks} model={model} />
            </StyledAPContainer>
        </>
    );
};

export default Contract;
